import React, { useState } from 'react';

// Checks that map to a single deal/contact field — confirming writes the
// verified number straight onto the record. Everything else is a
// document-to-document comparison: review-only, you correct it yourself.
const WRITE_BACK_KEYS = new Set(['fico', 'liquid_stated', 'asis', 'arv', 'purchase']);

// Severity → pill colors, using the portal's existing status palette.
const SEV = {
  Critical: { bg: '#F8D7DA', color: '#721C24' },
  Major:    { bg: '#FDF0DC', color: '#7A4A08' },
  Moderate: { bg: '#FDF0DC', color: '#7A4A08' },
  Minor:    { bg: '#F1EFE8', color: '#5F5E5A' },
};

const s = {
  card:        { background: '#fff', borderRadius: '10px', border: '0.5px solid #B8D4F0', overflow: 'hidden', marginBottom: '14px' },
  cardHead:    { background: '#4A88C8', padding: '9px 16px', display: 'flex', alignItems: 'center', gap: '7px', borderBottom: '2px solid #7AADE0' },
  cardTitle:   { color: '#fff', fontSize: '12px', fontWeight: 700, letterSpacing: '0.04em' },
  headPill:    { fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '5px', background: 'rgba(255,255,255,0.22)', color: '#fff', whiteSpace: 'nowrap' },
  collapseBtn: { background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: '14px', padding: 0 },
  cardBody:    { padding: '16px' },

  subLabel:    { fontSize: '10px', fontWeight: 700, color: '#4A88C8', letterSpacing: '0.07em', textTransform: 'uppercase' },
  subHint:     { fontSize: '11px', color: '#8a9ab5', margin: '6px 0 12px' },
  emptyText:   { fontSize: '13px', color: '#8a9ab5' },

  runRow:      { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', marginBottom: '4px' },
  runBtn:      { display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'transparent', color: '#2B72C2', border: '1.5px solid #B8D4F0', borderRadius: '7px', padding: '6px 13px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  runBtnBusy:  { opacity: 0.6, cursor: 'wait' },
  msg:         { fontSize: '12px', marginTop: '10px' },
  ok:          { color: '#2D5E0E' },
  err:         { color: '#c0392b' },

  clean:       { fontSize: '13px', color: '#2D5E0E', background: '#E4F2DA', borderRadius: '8px', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '8px' },

  row:         { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', background: '#fff', border: '0.5px solid #B8D4F0', borderRadius: '8px', marginBottom: '10px' },
  rowMain:     { flex: 1, minWidth: 0 },
  rowTitleLine:{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' },
  rowTitle:    { fontSize: '13px', fontWeight: 700, color: '#0D2545' },
  sevPill:     (c) => ({ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '4px', background: c.bg, color: c.color, whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.03em' }),
  values:      { fontSize: '12px', color: '#5F5E5A' },
  strong:      { color: '#0D2545', fontWeight: 600 },
  variance:    { color: '#8a9ab5' },
  reviewNote:  { fontSize: '11px', color: '#8a9ab5', marginTop: '5px', display: 'flex', alignItems: 'center', gap: '5px' },
  actions:     { display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0 },
  updateBtn:   { background: '#2B72C2', color: '#fff', border: 'none', borderRadius: '6px', padding: '7px 12px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  reviewBtn:   { background: 'transparent', color: '#2B72C2', border: '1.5px solid #B8D4F0', borderRadius: '6px', padding: '7px 12px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  dismissBtn:  { background: 'transparent', color: '#8a9ab5', border: '1px solid #D3D1C7', borderRadius: '6px', padding: '6px 12px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },

  resolvedBar: { background: '#F5F9FE', border: '0.5px solid #EAF1FA', borderRadius: '8px', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '9px', cursor: 'pointer', userSelect: 'none' },
  resolvedText:{ flex: 1, fontSize: '12px', color: '#5F5E5A' },
  resolvedItem:{ fontSize: '12px', color: '#0D2545', padding: '8px 14px', borderBottom: '1px solid #EAF1FA' },
  resolvedMeta:{ fontSize: '11px', color: '#8a9ab5', marginTop: '2px' },
};

// Controlled component. All discrepancy data + actions live in DealDetail so
// the reconcile bars (Deal / Borrower tabs) and the inline field notes share
// one store — resolving in any of those places updates this list too.
export default function DiscrepanciesCard({ items = [], loading, running, busyId, msg, err, onRun, onResolve, collapsed, onToggle }) {
  const [showResolved, setShowResolved] = useState(false);

  const open = items.filter((d) => d.status === 'Open');
  const resolved = items.filter((d) => d.status !== 'Open');

  return (
    <div style={s.card}>
      <div style={s.cardHead}>
        <i className="ti ti-robot" style={{ color: '#fff', fontSize: '14px' }} />
        <div style={{ ...s.cardTitle, flex: 1 }}>AI REVIEW</div>
        {open.length > 0 && <span style={s.headPill}>{open.length} to review</span>}
        <button style={s.collapseBtn} onClick={() => onToggle && onToggle('disc')}>
          <i className={`ti ${collapsed ? 'ti-chevron-right' : 'ti-chevron-down'}`} />
        </button>
      </div>

      {!collapsed && (
        <div style={s.cardBody}>
          <div style={s.runRow}>
            <div style={s.subLabel}>Cross-document checks</div>
            <button
              style={{ ...s.runBtn, ...(running ? s.runBtnBusy : {}) }}
              onClick={onRun}
              disabled={running}
            >
              <i className={`ti ${running ? 'ti-loader-2' : 'ti-refresh'}`} style={{ fontSize: '13px' }} />
              {running ? 'Checking…' : 'Run check'}
            </button>
          </div>
          <div style={s.subHint}>
            Compares this deal's documents against each other and the deal card. Run it after documents have been AI-reviewed. Field-level findings also appear on the Deal and Borrower tabs.
          </div>

          {msg && <div style={{ ...s.msg, ...s.ok }}>{msg}</div>}
          {err && <div style={{ ...s.msg, ...s.err }}>{err}</div>}

          <div style={{ marginTop: '12px' }}>
            {loading ? (
              <div style={s.emptyText}>Loading…</div>
            ) : items.length === 0 ? (
              <div style={s.clean}>
                <i className="ti ti-circle-check" style={{ fontSize: '15px' }} />
                Nothing checked yet. Run the check once documents are reviewed.
              </div>
            ) : open.length === 0 && resolved.length > 0 ? (
              <div style={s.clean}>
                <i className="ti ti-circle-check" style={{ fontSize: '15px' }} />
                All checks reviewed — nothing open.
              </div>
            ) : (
              open.map((d) => {
                const sev = SEV[d.severity] || SEV.Minor;
                const canWriteBack = WRITE_BACK_KEYS.has(d.checkKey);
                const busy = busyId === d.id;
                return (
                  <div key={d.id} style={s.row}>
                    <div style={s.rowMain}>
                      <div style={s.rowTitleLine}>
                        <span style={s.rowTitle}>{d.fieldChecked}</span>
                        <span style={s.sevPill(sev)}>{d.severity}</span>
                      </div>
                      <div style={s.values}>
                        Stated <span style={s.strong}>{d.statedValue || '—'}</span>
                        {'  ·  '}
                        Verified <span style={s.strong}>{d.verifiedValue || '—'}</span>
                        {d.variance && <>{'  '}<span style={s.variance}>({d.variance})</span></>}
                      </div>
                      {!canWriteBack && (
                        <div style={s.reviewNote}>
                          <i className="ti ti-eye" style={{ fontSize: '12px' }} />
                          Review only — open the documents and correct the rows yourself.
                        </div>
                      )}
                    </div>

                    <div style={s.actions}>
                      {canWriteBack ? (
                        <>
                          <button style={s.updateBtn} disabled={busy}
                            onClick={() => onResolve(d, 'use_verified')}>
                            {busy ? 'Working…' : `Update to ${d.verifiedValue}`}
                          </button>
                          <button style={s.dismissBtn} disabled={busy}
                            onClick={() => onResolve(d, 'ignore', 'Dismissed — stated value kept.')}>
                            Dismiss
                          </button>
                        </>
                      ) : (
                        <button style={s.reviewBtn} disabled={busy}
                          onClick={() => onResolve(d, 'keep_stated', 'Reviewed.')}>
                          {busy ? 'Working…' : 'Mark reviewed'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {resolved.length > 0 && (
            <div style={{ marginTop: open.length ? '4px' : '12px' }}>
              <div style={s.resolvedBar} onClick={() => setShowResolved((v) => !v)}>
                <i className="ti ti-circle-check" style={{ color: '#3B6D11', fontSize: '16px' }} />
                <div style={s.resolvedText}>
                  <span style={{ color: '#0D2545', fontWeight: 600 }}>{resolved.length} resolved</span>
                </div>
                <span style={{ color: '#2B72C2', fontSize: '11px', fontWeight: 600 }}>
                  {showResolved ? 'Hide' : 'Show'}
                </span>
              </div>
              {showResolved && (
                <div style={{ marginTop: '8px', border: '0.5px solid #EAF1FA', borderRadius: '8px', overflow: 'hidden' }}>
                  {resolved.map((d) => (
                    <div key={d.id} style={s.resolvedItem}>
                      <span style={{ fontWeight: 600 }}>{d.fieldChecked}</span>
                      <div style={s.resolvedMeta}>
                        {d.statedValue} → {d.verifiedValue}
                        {d.resolutionNote ? ` · ${d.resolutionNote}` : ''}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
